import { FC } from "react";
import styled from "@emotion/styled";
import { Button, Divider, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FieldValues, useForm } from "react-hook-form";
import { AuthService } from "@service/auth.service";
import ValidationError from "@components/ValidationError/ValidationError";
import { setMessage } from "@store/snackbarSlice";
import PublicForm from "@components/PublicForm";
import CustomLabelInput from "@components/CustomLabelInput";
import SocialSignIn from "@components/Onboarding/SocialSignIn";
import PassportInput from "@components/PassportInput";
import TermsOfService from "@components/TermsOfService";

const ForgotPassword = styled.div`
  width: 100%;
  font-size: 12px;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;

  > a {
    text-decoration: none;
    color: #000;
  }
`;

const authService = AuthService.instance;

interface SignInFormType {
  email: string;
  password: string;
}

const SignInForm: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<SignInFormType>();

  const signWithEmailAndPassword = async ({
    email,
    password,
  }: FieldValues): Promise<void> => {
    if (!email || !password) {
      return;
    }
    try {
      await authService.signWithEmailAndPassword(email, password);
    } catch (e) {
      const type =
        (e as any).name === "NotAuthorizedException"
          ? "incorrectEmailOrPassword"
          : "unknown";
      dispatch(setMessage({ content: t(`errors.${type}`), severity: "error" }));
    }
  };

  return (
    <PublicForm
      title={t("signIn.title", { appName: process.env.REACT_APP_APP_NAME })}
      subtitle={t("signIn.subtitle")}
    >
      <SocialSignIn onSignInClick={clearErrors} />

      <Divider
        style={{
          width: "100%",
          color: "#828282",
        }}
      >
        {t("signIn.orSeparator")}
      </Divider>

      <PublicForm.PublicFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(signWithEmailAndPassword)(e);
        }}
      >
        <CustomLabelInput label={t("signIn.emailInput.label")} id="email">
          <TextField
            {...register("email", {
              required: true,
            })}
            id="email"
            error={!!errors?.email}
            type="email"
            size="small"
            variant="outlined"
            fullWidth
            placeholder={t("signIn.emailInput.placeholder")}
          />
        </CustomLabelInput>

        <div>
          <ForgotPassword>
            <Link to="/reset-password">{t("signIn.forgotPassword")}</Link>
          </ForgotPassword>

          <CustomLabelInput
            label={t("signIn.passwordInput.label")}
            id="password"
          >
            <PassportInput
              {...register("password", {
                required: true,
              })}
              id="password"
              error={!!errors?.password}
              type="password"
              size="small"
              variant="outlined"
              fullWidth
              placeholder={t("signIn.passwordInput.placeholder")}
            />
          </CustomLabelInput>
        </div>
        {errors.root && (
          <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
        )}

          <Button
            id='signin-button'
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            fullWidth
            sx={{ marginTop: '15px' }}
          >
            {t("signIn.emailSignInButton")}
          </Button>
      </PublicForm.PublicFormContainer>

      <Divider
        style={{
          width: "100%",
          color: "#828282",
          marginTop: '40px',
        }}
      >
        {t("signIn.orSeparator")}
      </Divider>

      <PublicForm.PublicFormContainer
      >
        <Button
            id='invitation-button'
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            fullWidth
            sx={{ backgroundColor: '#FF7F50' }}
            onClick={() => navigate("/invite")}
          >
            {t("signIn.invitationButton")}
          </Button>
      </PublicForm.PublicFormContainer>

      <PublicForm.PublicFormFooter>
        <span>{t("signIn.noAccountLabel")}</span>
        <PublicForm.PublicFormLink to="/sign-up">
          {t("signIn.signUpLink")}
        </PublicForm.PublicFormLink>
        <PublicForm.PublicFormTos>
          <TermsOfService />
        </PublicForm.PublicFormTos>
      </PublicForm.PublicFormFooter>
    </PublicForm>
  );
};

export default SignInForm;
